/**
 * Cálculos puros del presupuesto que el comercial descarga desde el carrito.
 * Trabaja sobre las líneas tal y como las devuelve el api del carrito
 * (sellingPrice = precio neto del cliente, basePrice = precio de tarifa,
 * tax = % de impuesto de la línea, equivalenceCharge = lleva recargo).
 *
 * Todo el documento sale de estas líneas: el neto se redondea a céntimos (con
 * ofertas de carrito el api lo da sin redondear: 3,2130), el importe es neto x
 * uds y los totales son la suma de lo que se pinta. Con recargo de equivalencia
 * el api manda en `tax` IVA + RE juntos (26,2 = 21 + 5,2): aquí se separan.
 */

export const DIAS_VALIDEZ_PRESUPUESTO = 15;

export const round2 = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

/** IVA + recargo de equivalencia que el api manda sumados en `tax`. */
const RECARGO_POR_TOTAL = {
  26.2: { vat: 21, re: 5.2 },
  11.4: { vat: 10, re: 1.4 },
  4.5: { vat: 4, re: 0.5 },
  5.62: { vat: 5, re: 0.62 },
  2.26: { vat: 2, re: 0.26 }
};

/** { vat, re } de una línea: separa el recargo cuando la línea lo lleva. */
export const lineTaxes = (product) => {
  const tax = round2(product?.tax || 0);
  if (product?.equivalenceCharge && RECARGO_POR_TOTAL[tax]) {
    return RECARGO_POR_TOTAL[tax];
  }
  return { vat: tax, re: 0 };
};

/** Precio neto unitario, redondeado a céntimos. */
export const lineNetPrice = (product) => round2(product?.sellingPrice || 0);

/** Precio de tarifa unitario, redondeado a céntimos. */
export const lineBasePrice = (product) => round2(product?.basePrice || product?.sellingPrice || 0);

/** Descuento en % de una línea respecto a la tarifa (null si no hay descuento). */
export const lineDiscountPercent = (product) => {
  const base = lineBasePrice(product);
  const selling = lineNetPrice(product);
  if (base <= 0 || selling >= base) return null;
  return ((1 - selling / base) * 100);
};

/** Importe neto (sin impuestos) de una línea. Los regalos de pack van a 0. */
export const lineNetAmount = (product) => {
  if (product?.packOrigin) return 0;
  return round2(lineNetPrice(product) * (Number(product?.quantity) || 0));
};

const groupBy = (products, rateOf) => {
  const byRate = new Map();
  products.forEach((product) => {
    const net = lineNetAmount(product);
    const rate = rateOf(product);
    if (net <= 0 || !rate) return;
    const current = byRate.get(rate) ?? { rate, base: 0, amount: 0 };
    current.base = round2(current.base + net);
    byRate.set(rate, current);
  });
  return [...byRate.values()]
    .map((row) => ({ ...row, amount: round2(row.base * row.rate / 100) }))
    .sort((a, b) => b.rate - a.rate);
};

/** Desglose del IVA por tipo: [{ rate, base, amount }] de mayor a menor. */
export const vatBreakdown = (products = []) => groupBy(products, (p) => lineTaxes(p).vat);

/** Desglose del recargo de equivalencia por tipo (vacío si ninguna línea lo lleva). */
export const equivalenceBreakdown = (products = []) => groupBy(products, (p) => lineTaxes(p).re);

/** Totales del presupuesto a partir de las líneas que se pintan. */
export const quoteTotals = (products = []) => {
  const vat = vatBreakdown(products);
  const re = equivalenceBreakdown(products);
  const base = round2(products.reduce((sum, p) => sum + lineNetAmount(p), 0));
  const discount = round2(products.reduce((sum, p) => (
    p?.packOrigin ? sum : sum + Math.max(0, lineBasePrice(p) - lineNetPrice(p)) * (Number(p?.quantity) || 0)
  ), 0));
  const taxes = [...vat, ...re].reduce((sum, row) => sum + row.amount, 0);
  return { base, discount, vat, re, total: round2(base + taxes) };
};

/** Referencia legible del presupuesto: PRE-AAAAMMDD-HHMM-<cliente>. */
export const quoteReference = (numCliente, date = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  const stamp = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
  const time = `${pad(date.getHours())}${pad(date.getMinutes())}`;
  return `PRE-${stamp}-${time}${numCliente ? `-${numCliente}` : ''}`;
};

export const quoteValidUntil = (date = new Date(), days = DIAS_VALIDEZ_PRESUPUESTO) => {
  const until = new Date(date);
  until.setDate(until.getDate() + days);
  return until;
};
